import React from "react";
import { BlockStack, Box, ContextualSaveBar, Grid, Page } from "@shopify/polaris";
import { BarChart3, Bot, CreditCard, Paintbrush, Sparkles } from "lucide-react";
import { ReachLogo } from "../../Logo";
import type { SettingsTab } from "../constants";

type SettingsLayoutProps = {
  activeTabId: string;
  children: React.ReactNode;
  density: "comfortable" | "compact";
  dirtyMessage: string;
  hero?: { title: string; subtitle: string; insight?: string; icon: React.ReactNode };
  isDirty: boolean;
  loading: boolean;
  onDiscard: () => void;
  onSave: () => void;
  safeSelectedTab: number;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
  tabs: SettingsTab[];
  variant?: SettingsLayoutVariant;
  visualEditor: React.ReactNode;
};

export type SettingsLayoutVariant = "dashboard" | "headless";

const TAB_ICONS: Record<string, React.ReactNode> = {
  "visual-editor": <Paintbrush size={17} />,
  assistant: <Bot size={17} />,
  analytics: <BarChart3 size={17} />,
  plans: <CreditCard size={17} />,
  designer: <Sparkles size={17} />,
};

export function SettingsLayout({
  activeTabId,
  children,
  density,
  dirtyMessage,
  hero,
  isDirty,
  loading,
  onDiscard,
  onSave,
  safeSelectedTab,
  setSelectedTab,
  tabs,
  variant = "dashboard",
  visualEditor,
}: SettingsLayoutProps) {
  const isHeadless = variant === "headless";
  const workspaceHeight = isHeadless ? "100vh" : "calc(100vh - 72px)";
  const workspaceMinHeight = isHeadless ? "0" : "680px";
  const activeTab = tabs[safeSelectedTab];

  const tabNav = (
    <nav className="theme-settings-nav-list" aria-label="Workspace sections">
      {tabs.map((tab, index) => {
        const isActive = safeSelectedTab === index;
        return (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(index)}
            className="theme-settings-tab"
            data-active={isActive ? "true" : "false"}
            type="button"
          >
            <span className="theme-settings-tab-icon">{TAB_ICONS[tab.id] || null}</span>
            <span>{tab.content}</span>
          </button>
        );
      })}
    </nav>
  );

  const shellMarkup = (
    <div
      className={`theme-settings-shell${isHeadless ? " theme-settings-shell--headless" : ""}`}
      data-density={density}
      data-variant={variant}
    >
      {!isHeadless && (
      <aside className="theme-settings-nav">
        <div className="theme-settings-brand">
          <span className="theme-settings-brand-mark">
            <ReachLogo size="large" showText={false} color="rgba(255,255,255,0.95)" detailColor="var(--reach-logo-detail)" />
          </span>
          <span className="theme-settings-brand-text">REACH<span className="theme-settings-brand-dot">.</span></span>
        </div>
        <div className="theme-settings-nav-label">Workspace</div>
        {tabNav}
      </aside>
      )}

      <section className="theme-settings-body">
        <div
          className="theme-visual-editor-wrap"
          style={{
            height: workspaceHeight,
            minHeight: workspaceMinHeight,
            display: activeTab.id === "visual-editor" ? "flex" : "none",
          }}
        >
          {visualEditor}
        </div>

        {activeTab.id !== "visual-editor" && (
          <div
            className="theme-content-wrap"
            data-tab={activeTabId}
            style={{
              padding: isHeadless ? "1rem" : "1.25rem",
              height: workspaceHeight,
              minHeight: workspaceMinHeight,
              overflowY: "auto",
            }}
          >
            {hero && (
              <div className="theme-tab-hero" data-tab={activeTabId}>
                <div className="theme-tab-hero-inner">
                  <span className="theme-tab-hero-icon">{hero.icon}</span>
                  <div>
                    <h2 className="theme-tab-hero-title">{hero.title}</h2>
                    <p className="theme-tab-hero-subtitle">{hero.subtitle}</p>
                  </div>
                </div>
                {hero.insight && <span className="theme-tab-hero-chip">{hero.insight}</span>}
              </div>
            )}
            <BlockStack gap={density === "compact" ? "300" : "400"}>
              <Grid>
                <Grid.Cell columnSpan={{ xs: 6, lg: 12 }}>
                  <div style={{ width: "100%" }}>
                    <Box minHeight="600px">{children}</Box>
                  </div>
                </Grid.Cell>
              </Grid>
              <Box paddingBlockEnd="800" />
            </BlockStack>
          </div>
        )}
      </section>
    </div>
  );

  return (
    <>
      {isDirty && (
        <ContextualSaveBar
          message={dirtyMessage}
          saveAction={{ onAction: onSave, loading }}
          discardAction={{ onAction: onDiscard }}
        />
      )}
      {isHeadless ? shellMarkup : <Page fullWidth>{shellMarkup}</Page>}
    </>
  );
}
